import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QRCodeStyling from "qr-code-styling";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── GarzónBot branded QR options — SINGLE SOURCE OF TRUTH used by BOTH
// the on-screen preview and the downloadable high-res PNG. Any tweak here
// affects both simultaneously → WYSIWYG for the restaurant.
const _GARZON_MASCOT_URL = typeof window !== "undefined"
  ? `${window.location.origin}/garzon-mascot.png`
  : "/garzon-mascot.png";

function garzonQrOptions(url, size) {
  return {
    width: size,
    height: size,
    type: "canvas",
    data: url,
    margin: 0,
    qrOptions: { errorCorrectionLevel: "H" },
    backgroundOptions: { color: "#FFFFFF" },
    dotsOptions: { color: "#FF6600", type: "classy-rounded" },
    cornersSquareOptions: { color: "#FF6600", type: "extra-rounded" },
    cornersDotOptions: { color: "#FF6600", type: "dot" },
    image: _GARZON_MASCOT_URL,
    imageOptions: {
      crossOrigin: "anonymous",
      margin: 8,
      imageSize: 0.28,
      hideBackgroundDots: true,
    },
  };
}

// ── On-screen branded QR — same visual style as the ZIP download so the
// restaurant sees exactly what they will print. Renders lazily on mount.
function BrandedQR({ value, size = 180 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = "";
    const qr = new QRCodeStyling(garzonQrOptions(value, size));
    qr.append(ref.current);
  }, [value, size]);
  return <div ref={ref} style={{ width: size, height: size }} data-testid="garzon-branded-qr" />;
}

const STATUS_META = {
  nuevo:    { label: "🔴 Nuevo",   chip: "bg-red-50 text-red-700 border-red-200" },
  en_prep:  { label: "🟡 En prep", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  enviado:  { label: "🟢 Enviado", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function GarzonPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [comandas, setComandas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingMenu, setUploadingMenu] = useState(false);
  const [tab, setTab] = useState("config");
  const [openTooltip, setOpenTooltip] = useState(null);
  const [openStatusMenu, setOpenStatusMenu] = useState(null);
  const [showHistorial, setShowHistorial] = useState(false);
  // Audio alert for new orders. We persist the mute preference in
  // localStorage so the kitchen tablet keeps the same setting between
  // page loads / shift changes.
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("garzon_comandas_muted") === "1"; } catch { return false; }
  });
  // Tracks the IDs we have already seen so we only chime for *truly* new
  // 🔴 Nuevo comandas (not for the initial baseline fetch, not for status
  // transitions). `null` means "baseline not yet established".
  const seenComandaIdsRef = useRef(null);
  const audioRef = useRef(null);
  // Mirror `muted` into a ref so the interval-driven `fetchComandas`
  // (which closes over the first render's value via [] deps) can read
  // the up-to-date preference.
  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  const [form, setForm] = useState({
    nombre_bot: "GarzónBot",
    nombre_restaurante: "",
    num_mesas: 10,
    whatsapp_numero: "",
    google_sheets_id: "",
    openai_api_key: "",
    menu: "",
    activo: true,
  });

  useEffect(() => { fetchAll(); }, []);

  // Always-on auto-refresh: every 15 seconds re-fetch comandas + stats so
  // the kitchen sees new orders without having to reload the page. Runs
  // regardless of the currently visible tab so stats badges and comandas
  // counter stay fresh.
  useEffect(() => {
    const id = setInterval(() => { fetchComandas(); fetchStats(); }, 15000);
    return () => clearInterval(id);
  }, []);

  // Immediate refresh when the user switches to the Comandas tab so they
  // don't have to wait for the next interval tick to see new orders.
  useEffect(() => {
    if (tab === "comandas") { fetchComandas(); fetchStats(); }
  }, [tab]);

  async function fetchAll() {
    setLoading(true);
    try {
      await Promise.all([fetchConfig(), fetchComandas(), fetchStats()]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchConfig() {
    const res = await fetch(`${API}/api/garzon/config`, { credentials: "include" });
    const data = await res.json();
    if (data.config) {
      setConfig(data.config);
      setForm(f => ({ ...f, ...data.config, openai_api_key: "" }));
    }
  }

  async function fetchComandas() {
    const res = await fetch(`${API}/api/garzon/comandas`, { credentials: "include" });
    const data = await res.json();
    const next = data.comandas || [];

    // Detect newly arrived comandas in status `nuevo` and chime once for
    // the batch (no matter how many arrived in the same poll). The first
    // ever fetch only seeds the baseline so we don't chime for orders
    // that were already on screen when the page loaded.
    const nextIds = new Set(next.map(c => c.comanda_id));
    const prevIds = seenComandaIdsRef.current;
    if (prevIds !== null) {
      const newNuevos = next.filter(c => c.status === "nuevo" && !prevIds.has(c.comanda_id));
      if (newNuevos.length > 0) playComandaChime();
    }
    seenComandaIdsRef.current = nextIds;
    setComandas(next);
  }

  function playComandaChime() {
    if (mutedRef.current) return;
    try {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = 0;
      // play() returns a promise that can reject if the browser hasn't
      // received a user gesture yet (autoplay policy). Swallow that.
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      /* no-op */
    }
  }

  function toggleMuted() {
    setMuted(prev => {
      const v = !prev;
      try { localStorage.setItem("garzon_comandas_muted", v ? "1" : "0"); } catch { /* ignore */ }
      // If the user just unmuted, give an audible confirmation so they
      // know the speaker actually works on this device.
      if (!v) {
        setTimeout(() => {
          try {
            const a = audioRef.current;
            if (a) { a.currentTime = 0; const p = a.play(); if (p && typeof p.catch === "function") p.catch(() => {}); }
          } catch { /* no-op */ }
        }, 50);
      }
      return v;
    });
  }

  async function fetchStats() {
    const res = await fetch(`${API}/api/garzon/stats`, { credentials: "include" });
    const data = await res.json();
    setStats(data.stats);
  }

  async function saveConfig() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/garzon/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Configuración guardada");
        await fetchConfig();
      } else {
        toast.error(data.detail || "Error al guardar");
      }
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActivo() {
    const res = await fetch(`${API}/api/garzon/config/toggle`, { method: "PATCH", credentials: "include" });
    const data = await res.json();
    if (data.success) {
      setConfig(c => ({ ...c, activo: data.activo }));
      setForm(f => ({ ...f, activo: data.activo }));
      toast.success(data.activo ? "GarzónBot activado" : "GarzónBot pausado");
    }
  }

  async function uploadMenuPDF(fileObj) {
    if (!fileObj) return;
    setUploadingMenu(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await fetch(`${API}/api/garzon/menu/upload-pdf`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      // Try JSON first; if it fails fall back to raw text so we still
      // surface the backend error instead of a generic "Error al subir el PDF".
      let data, rawText;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        data = await res.json();
      } else {
        rawText = await res.text();
      }
      if (!res.ok) {
        const detail = data?.detail || rawText || `HTTP ${res.status}`;
        toast.error(`No se pudo procesar el PDF: ${detail}`);
        console.error("[GarzonPage] upload-pdf failed", res.status, data || rawText);
        return;
      }
      setForm(f => ({ ...f, menu: data.menu_text || JSON.stringify(data.menu_json, null, 2) }));
      toast.success("Menú extraído. Revísalo y haz clic en 'Guardar configuración'.");
    } catch (e) {
      toast.error(`Error al subir el PDF: ${e?.message || e}`);
      console.error("[GarzonPage] upload-pdf exception", e);
    } finally {
      setUploadingMenu(false);
    }
  }

  async function updateComandaStatus(comandaId, newStatus) {
    setOpenStatusMenu(null);
    // Optimistic update
    setComandas(prev => prev.map(c => c.comanda_id === comandaId ? { ...c, status: newStatus } : c));
    const res = await fetch(`${API}/api/garzon/comandas/${comandaId}/status`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });
    if (!res.ok) {
      toast.error("No se pudo actualizar el estado");
      await fetchComandas();
    } else {
      await fetchStats();
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white">
        <div className="text-gray-500">Cargando GarzónBot…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="garzon-back-btn"
              className="text-sm text-gray-500 hover:text-gray-800"
            >
              ← Dashboard
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-950 flex items-center gap-2">
                🍽️ GarzónBot
              </h1>
              <p className="text-sm text-gray-500">Asistente para restaurantes y bares</p>
            </div>
          </div>
          {config && (
            <button
              onClick={toggleActivo}
              data-testid="garzon-toggle-activo"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                config.activo
                  ? "bg-emerald-500 text-white hover:bg-emerald-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {config.activo ? "● Activo" : "○ Inactivo"}
            </button>
          )}
        </div>

        {/* Tutorial video */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm" data-testid="garzon-tutorial-video">
          <h3 className="text-base font-semibold text-gray-900 mb-3">¿Cómo configurar tu GarzónBot?</h3>
          <div style={{ position: 'relative', paddingTop: '56.25%' }}>
            <iframe
              src="https://player.mediadelivery.net/embed/672206/2e950b1f-ae1a-4696-ae3f-32308a0d95e6?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
              loading="lazy"
              style={{ border: 0, position: 'absolute', top: 0, height: '100%', width: '100%' }}
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen;"
              allowFullScreen
              title="Cómo configurar tu GarzónBot"
            />
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatCard label="Pedidos hoy" value={stats.pedidos_hoy} accent="text-gray-900" />
            <StatCard label="Mesas activas" value={stats.mesas_activas} accent="text-emerald-700" />
            <StatCard label="🔴 Nuevos" value={stats.nuevos} accent="text-red-600" />
            <StatCard label="🟡 En prep" value={stats.en_prep} accent="text-amber-600" />
            <StatCard label="🟢 Enviados" value={stats.enviados} accent="text-emerald-600" />
          </div>
        )}

        {/* Info card */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6" data-testid="garzon-whatsapp-info">
          <div className="flex items-start gap-3">
            <div className="text-2xl">📱</div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-emerald-900 mb-1">Conexión a WhatsApp</h3>
              <p className="text-sm text-emerald-800 mb-3">
                Para activar tu GarzónBot en WhatsApp, ve al Dashboard y haz clic en
                <strong> "Activar Asistente"</strong> en la tarjeta de tu bot GarzónBot. Allí podrás escanear el código QR.
              </p>
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="garzon-go-to-dashboard-btn"
                className="inline-flex items-center gap-2 bg-emerald-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-emerald-700 transition-colors text-sm"
              >
                Ir al Dashboard →
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 mb-6">
          <TabBtn id="config" current={tab} onClick={setTab}>Configuración</TabBtn>
          <TabBtn id="comandas" current={tab} onClick={setTab}>Comandas {comandas.length ? `(${comandas.length})` : ""}</TabBtn>
          <TabBtn id="mesas" current={tab} onClick={setTab}>Mis Mesas</TabBtn>
        </div>

        {tab === "config" && (
          <ConfigForm
            form={form}
            setForm={setForm}
            saving={saving}
            saveConfig={saveConfig}
            openTooltip={openTooltip}
            setOpenTooltip={setOpenTooltip}
            uploadMenuPDF={uploadMenuPDF}
            uploadingMenu={uploadingMenu}
          />
        )}

        {tab === "comandas" && (
          <ComandasTable
            comandas={comandas}
            openStatusMenu={openStatusMenu}
            setOpenStatusMenu={setOpenStatusMenu}
            updateComandaStatus={updateComandaStatus}
            showHistorial={showHistorial}
            setShowHistorial={setShowHistorial}
            muted={muted}
            toggleMuted={toggleMuted}
          />
        )}

        {/* Hidden audio element used to chime when a new 🔴 Nuevo comanda
            arrives. Kept at the top-level layout so it survives tab switches
            and is always preloaded. */}
        <audio
          ref={audioRef}
          src={`${process.env.PUBLIC_URL || ""}/sounds/comanda-nuevo.mp3`}
          preload="auto"
          data-testid="garzon-comanda-audio"
        />

        {tab === "mesas" && (
          <MesasGrid
            numMesas={parseInt(form.num_mesas) || 10}
            whatsapp={form.whatsapp_numero}
            nombreRestaurante={form.nombre_restaurante}
          />
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

function TabBtn({ id, current, onClick, children }) {
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`garzon-tab-${id}`}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        current === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

function ConfigForm({ form, setForm, saving, saveConfig, openTooltip, setOpenTooltip, uploadMenuPDF, uploadingMenu }) {
  const fields = [
    { key: "nombre_bot", label: "Nombre del bot", placeholder: "GarzónBot" },
    { key: "nombre_restaurante", label: "Nombre del restaurante", placeholder: "La Trattoria" },
    { key: "num_mesas", label: "Número de mesas", placeholder: "10", type: "number" },
    {
      key: "whatsapp_numero",
      label: "WhatsApp del restaurante (sin +)",
      placeholder: "56912345678",
      helpText: "Solo dígitos, incluyendo código de país. Ej: para Chile +569 1234 5678 escribe 56912345678.",
    },
    {
      key: "google_sheets_id",
      label: "Google Sheets ID",
      placeholder: "1abc...XYZ",
      help: {
        title: "¿Cómo obtenerlo? (importante: 2 pasos)",
        steps: [
          { text: "Crea una hoja en ", link: { href: "https://sheets.google.com", label: "sheets.google.com" } },
          { text: 'Haz clic en "Compartir" arriba a la derecha' },
          { text: "Pega este email: garzonbot-service@garzonbot.iam.gserviceaccount.com" },
          { text: 'Permiso: "Editor" → Enviar' },
          { text: 'Copia el ID de la URL: docs.google.com/spreadsheets/d/<<ESTE_ES_EL_ID>>/edit' },
          { text: "Pégalo aquí abajo" },
        ],
        warning: "⚠️ El paso 3+4 (compartir con garzonbot-service@garzonbot.iam.gserviceaccount.com como Editor) es obligatorio. Sin esto el bot no podrá escribir pedidos en tu Sheet.",
      },
    },
    {
      key: "openai_api_key",
      label: "OpenAI API Key",
      placeholder: "sk-proj-...",
      type: "password",
      help: {
        title: "¿Cómo obtenerla?",
        steps: [
          { text: "Ve a ", link: { href: "https://platform.openai.com/api-keys", label: "platform.openai.com/api-keys" } },
          { text: "Inicia sesión o crea una cuenta" },
          { text: 'Haz clic en "Create new secret key"' },
          { text: "Copia la key (empieza con sk-proj-)" },
        ],
        warning: "⚠️ Guárdala en un lugar seguro — no podrás verla nuevamente",
      },
    },
    {
      key: "menu",
      label: "Menú del restaurante",
      type: "textarea",
      placeholder: `Ejemplo:
Pizza Margarita - $8.500
Pizza Cuatro Quesos - $9.900
Pasta Bolognese - $7.500
Cerveza Heineken - $3.000
Cerveza Artesanal IPA - $4.000`,
      helpText: "Lista todos los platos con sus precios. El bot usará esto para tomar pedidos y calcular totales.",
      fullWidth: true,
    },
  ];
  return (
    <div className="bg-white rounded-xl border p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map(({ key, label, placeholder, type, help, helpText, fullWidth }) => (
          <div key={key} className={fullWidth ? "md:col-span-2" : undefined}>
            <div className="flex items-center justify-between gap-1.5 mb-1">
              <div className="flex items-center gap-1.5">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                {help && (
                  <div className="relative inline-block group">
                    <button
                      type="button"
                      onClick={() => setOpenTooltip(openTooltip === key ? null : key)}
                      onBlur={() => setTimeout(() => setOpenTooltip(t => t === key ? null : t), 150)}
                      data-testid={`garzon-help-${key}`}
                      aria-label={`Ayuda para ${label}`}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold hover:bg-emerald-500 hover:text-white transition-colors cursor-help"
                    >
                      ?
                    </button>
                    <div
                      data-testid={`garzon-tooltip-${key}`}
                      className={`absolute z-30 left-0 top-6 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs text-gray-700 transition-opacity duration-150 ${
                        openTooltip === key ? "opacity-100 visible" : "opacity-0 invisible group-hover:opacity-100 group-hover:visible"
                      }`}
                    >
                      <p className="font-semibold text-gray-900 mb-2">{help.title}</p>
                      <ol className="list-decimal list-inside space-y-1.5">
                        {help.steps.map((s, i) => (
                          <li key={i} className="leading-snug">
                            {s.text}
                            {s.link && (
                              <a href={s.link.href} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline break-all">
                                {s.link.label}
                              </a>
                            )}
                          </li>
                        ))}
                      </ol>
                      {help.warning && (
                        <p className="mt-2 pt-2 border-t border-gray-100 text-amber-700">{help.warning}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {key === "menu" && (
                <label
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                    uploadingMenu
                      ? "bg-gray-100 text-gray-500 border-gray-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                  }`}
                  data-testid="garzon-menu-upload-label"
                >
                  📄 {uploadingMenu ? "Procesando…" : "Subir carta en PDF"}
                  <input
                    type="file"
                    accept="application/pdf"
                    disabled={uploadingMenu}
                    onChange={e => { const f = e.target.files?.[0]; if (f) { uploadMenuPDF(f); e.target.value = ""; } }}
                    data-testid="garzon-menu-upload-input"
                    className="hidden"
                  />
                </label>
              )}
            </div>
            {type === "textarea" ? (
              <textarea
                rows={6}
                value={form[key] || ""}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                data-testid={`garzon-input-${key}`}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y font-mono"
              />
            ) : (
              <input
                type={type || "text"}
                value={form[key] ?? ""}
                onChange={e => setForm(f => ({ ...f, [key]: type === "number" ? parseInt(e.target.value || "0") : e.target.value }))}
                placeholder={placeholder}
                data-testid={`garzon-input-${key}`}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            )}
            {helpText && <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{helpText}</p>}
          </div>
        ))}
      </div>
      <button
        onClick={saveConfig}
        disabled={saving}
        data-testid="garzon-save-btn"
        className="w-full bg-emerald-600 text-white py-2 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
      >
        {saving ? "Guardando..." : "Guardar configuración"}
      </button>
    </div>
  );
}

// Returns a stable YYYY-MM-DD key in the browser's local timezone so comandas
// can be grouped by "day as the user sees it" (not UTC).
function localDayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDayLabel(date) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const key = localDayKey(date);
  if (key === localDayKey(today)) return "Hoy";
  if (key === localDayKey(yesterday)) return "Ayer";
  // e.g. "lun 4 jun" in user's local timezone
  return date.toLocaleDateString("es-CL", { weekday: "short", day: "numeric", month: "short" });
}

function formatLocalTime(date) {
  return date.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function ComandasTable({ comandas, openStatusMenu, setOpenStatusMenu, updateComandaStatus, showHistorial, setShowHistorial, muted, toggleMuted }) {
  // Close the status dropdown when clicking anywhere outside of it.
  useEffect(() => {
    if (!openStatusMenu) return;
    const onDocClick = (e) => {
      if (!e.target.closest("[data-status-dropdown]")) setOpenStatusMenu(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openStatusMenu, setOpenStatusMenu]);

  // 1. Parse created_at into Date objects, filtering out malformed rows.
  // 2. Sort newest first.
  // 3. When the "Ver historial completo" toggle is OFF, hide enviadas that
  //    are older than 30 minutes so the kitchen view stays focused on
  //    active orders.
  const now = Date.now();
  const THIRTY_MIN_MS = 30 * 60 * 1000;
  const hiddenCount = comandas.reduce((n, c) => {
    const t = c.created_at ? new Date(c.created_at).getTime() : 0;
    return (c.status === "enviado" && t && now - t > THIRTY_MIN_MS) ? n + 1 : n;
  }, 0);

  const visible = comandas
    .map(c => ({ ...c, _date: c.created_at ? new Date(c.created_at) : null }))
    .filter(c => {
      if (showHistorial) return true;
      if (c.status !== "enviado") return true;
      if (!c._date) return true;
      return now - c._date.getTime() <= THIRTY_MIN_MS;
    })
    .sort((a, b) => {
      const ta = a._date ? a._date.getTime() : 0;
      const tb = b._date ? b._date.getTime() : 0;
      return tb - ta;
    });

  // Group consecutive comandas of the same local day into sections.
  const groups = [];
  for (const c of visible) {
    const key = c._date ? localDayKey(c._date) : "sin-fecha";
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(c);
    } else {
      groups.push({ key, date: c._date, items: [c] });
    }
  }

  const toggleBar = (
    <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-xl">
      <div className="text-xs text-gray-600" data-testid="garzon-comandas-counter">
        {visible.length} comanda{visible.length !== 1 ? "s" : ""} visible{visible.length !== 1 ? "s" : ""}
        {!showHistorial && hiddenCount > 0 && (
          <span className="ml-2 text-gray-400">· {hiddenCount} enviada{hiddenCount !== 1 ? "s" : ""} oculta{hiddenCount !== 1 ? "s" : ""}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={toggleMuted}
          data-testid="garzon-toggle-mute"
          title={muted ? "Reactivar sonido de comandas nuevas" : "Silenciar sonido de comandas nuevas"}
          aria-pressed={muted}
          className={`text-xs font-medium w-9 h-7 rounded-full border transition flex items-center justify-center ${
            muted
              ? "bg-gray-200 text-gray-500 border-gray-300 hover:bg-gray-300"
              : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
          }`}
        >
          {muted ? "🔇" : "🔔"}
        </button>
        <button
          onClick={() => setShowHistorial(!showHistorial)}
          data-testid="garzon-toggle-historial"
          className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${
            showHistorial
              ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
        >
          {showHistorial ? "Ocultar historial" : "Ver historial completo"}
        </button>
      </div>
    </div>
  );

  if (!comandas.length) {
    return (
      <div className="bg-white rounded-xl border p-8 text-center text-sm text-gray-500" data-testid="garzon-comandas-empty">
        Aún no hay comandas. Cuando un cliente escanee un QR de mesa y haga un pedido, aparecerá aquí.
      </div>
    );
  }

  if (!visible.length) {
    return (
      <div className="bg-white rounded-xl border" data-testid="garzon-comandas-table">
        {toggleBar}
        <div className="p-8 text-center text-sm text-gray-500" data-testid="garzon-comandas-all-hidden">
          Todas las comandas enviadas tienen más de 30 minutos. Activa <strong>“Ver historial completo”</strong> para mostrarlas.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border" data-testid="garzon-comandas-table">
      {toggleBar}
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-3 text-left">#</th>
            <th className="px-4 py-3 text-left">Mesa</th>
            <th className="px-4 py-3 text-left">Hora</th>
            <th className="px-4 py-3 text-left">Items</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-left">Estado</th>
          </tr>
        </thead>
        {groups.map(group => (
          <tbody key={group.key} className="divide-y divide-gray-100">
            <tr className="bg-gray-50/60" data-testid={`garzon-day-header-${group.key}`}>
              <td colSpan={6} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {group.date ? formatDayLabel(group.date) : "Sin fecha"}
                <span className="ml-2 text-gray-400 normal-case font-normal">
                  · {group.items.length} comanda{group.items.length !== 1 ? "s" : ""}
                </span>
              </td>
            </tr>
            {group.items.map(c => {
              const meta = STATUS_META[c.status] || STATUS_META.nuevo;
              const hora = c._date
                ? `${c._date.toLocaleDateString("es-CL", { day: "numeric", month: "short" })} — ${formatLocalTime(c._date)}`
                : "—";
              return (
                <tr key={c.comanda_id} data-testid={`garzon-comanda-row-${c.comanda_id}`}>
                  <td className="px-4 py-3 font-mono text-gray-600">#{String(c.numero_mesa || 0).padStart(3, "0")}</td>
                  <td className="px-4 py-3 font-semibold">Mesa {c.mesa}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{hora}</td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="text-gray-800 line-clamp-2 whitespace-pre-wrap">{c.items}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">${(c.total || 0).toLocaleString("es-CL")}</td>
                  <td className="px-4 py-3">
                    <div className="relative inline-block" data-status-dropdown>
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenStatusMenu(openStatusMenu === c.comanda_id ? null : c.comanda_id); }}
                        data-testid={`garzon-status-${c.comanda_id}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium ${meta.chip} hover:opacity-80 transition`}
                      >
                        {meta.label} ▾
                      </button>
                      {openStatusMenu === c.comanda_id && (
                        <div
                          className="absolute z-50 left-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-36"
                          data-testid={`garzon-status-menu-${c.comanda_id}`}
                        >
                          {Object.entries(STATUS_META).map(([k, v]) => (
                            <button
                              key={k}
                              onClick={() => updateComandaStatus(c.comanda_id, k)}
                              data-testid={`garzon-status-option-${c.comanda_id}-${k}`}
                              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${c.status === k ? "font-bold bg-gray-50" : ""}`}
                            >
                              {v.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}

function MesasGrid({ numMesas, whatsapp, nombreRestaurante }) {
  const containerRefs = useRef({});
  const cleanWa = (whatsapp || "").replace(/[^\d]/g, "");
  if (!cleanWa) {
    return (
      <div className="bg-white rounded-xl border p-8 text-center text-sm text-gray-500" data-testid="garzon-mesas-empty">
        Configura primero el número de WhatsApp del restaurante en la pestaña <strong>Configuración</strong> para generar los QR.
      </div>
    );
  }
  const mesas = Array.from({ length: Math.max(1, Math.min(numMesas, 200)) }, (_, i) => i + 1);

  // ── Generates a HIGH-RES branded PNG blob for printing. Uses the
  // qr-code-styling library with GarzónBot's brand identity hardcoded:
  //   • #FF6600 modules ("classy-rounded" style — rounded corners w/ small notches)
  //   • extra-rounded finder patterns (the 3 corner squares)
  //   • center logo (public mascot at /garzon-mascot.png, loaded same-origin)
  //   • H-level error correction (30% recovery — logo covers ~15% of the QR
  //     so we need every bit of redundancy we can get)
  // The final PNG is composed on a 1000×1000 canvas with the QR rendered
  // at 900×900 centered horizontally, leaving 100px at the bottom for a
  // discreet "Powered by ACBotHub" caption.
  async function highResBlob(url) {
    const QR_SIZE = 900;
    const CANVAS_W = 1000;
    const CANVAS_H = 1000;
    const CAPTION_H = 60;

    // Render the styled QR using the SHARED brand options so the on-screen
    // preview and the downloadable PNG are visually identical (WYSIWYG).
    const qr = new QRCodeStyling(garzonQrOptions(url, QR_SIZE));

    // qr-code-styling exposes getRawData() which returns a Blob asynchronously
    // once fonts / images are ready. We use that instead of poking internals.
    const qrBlob = await qr.getRawData("png");
    const qrDataUrl = URL.createObjectURL(qrBlob);
    const qrImg = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = qrDataUrl;
    });

    // Compose onto a wider canvas so we can add the caption below.
    const out = document.createElement("canvas");
    out.width = CANVAS_W;
    out.height = CANVAS_H;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    const qrX = (CANVAS_W - QR_SIZE) / 2;
    const qrY = (CANVAS_H - CAPTION_H - QR_SIZE) / 2;
    ctx.drawImage(qrImg, qrX, qrY, QR_SIZE, QR_SIZE);

    // "Powered by ACBotHub" — discreet, gray, centered under the QR.
    ctx.fillStyle = "#9CA3AF";
    ctx.font = "500 22px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Powered by ACBotHub", CANVAS_W / 2, CANVAS_H - CAPTION_H / 2 - 10);

    URL.revokeObjectURL(qrDataUrl);
    return await new Promise(resolve => out.toBlob(resolve, "image/png"));
  }

  async function downloadOne(n) {
    const url = `https://wa.me/${cleanWa}?text=${encodeURIComponent(`Mesa ${n}`)}`;
    try {
      const blob = await highResBlob(url);
      saveAs(blob, `mesa-${n}.png`);
    } catch (e) {
      // Fallback to the on-screen canvas if the hi-res render fails.
      console.warn(`[GarzonBot QR] hi-res render failed for mesa ${n}, falling back to on-screen canvas:`, e);
      const canvas = containerRefs.current[n]?.querySelector("canvas");
      if (canvas) canvas.toBlob(b => saveAs(b, `mesa-${n}.png`));
    }
  }

  async function downloadAll() {
    const zip = new JSZip();
    for (const n of mesas) {
      const url = `https://wa.me/${cleanWa}?text=${encodeURIComponent(`Mesa ${n}`)}`;
      try {
        const blob = await highResBlob(url);
        if (blob) zip.file(`mesa-${n}.png`, blob);
      } catch (e) {
        // Fallback to on-screen canvas for this table if hi-res fails.
        console.warn(`[GarzonBot QR] hi-res render failed for mesa ${n} in batch, falling back to on-screen canvas:`, e);
        const canvas = containerRefs.current[n]?.querySelector("canvas");
        if (!canvas) continue;
        const blob = await new Promise(res => canvas.toBlob(res));
        if (blob) zip.file(`mesa-${n}.png`, blob);
      }
    }
    const out = await zip.generateAsync({ type: "blob" });
    const fname = `qr-mesas-${(nombreRestaurante || "restaurante").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.zip`;
    saveAs(out, fname);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          {mesas.length} mesa{mesas.length !== 1 ? "s" : ""} — escanea con la cámara del celular y abre WhatsApp con el mensaje pre-cargado.
        </p>
        <button
          onClick={downloadAll}
          data-testid="garzon-download-all-btn"
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          📦 Descargar todos (ZIP)
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {mesas.map(n => {
          const url = `https://wa.me/${cleanWa}?text=${encodeURIComponent(`Mesa ${n}`)}`;
          return (
            <div
              key={n}
              ref={el => { if (el) containerRefs.current[n] = el; }}
              data-testid={`garzon-mesa-card-${n}`}
              className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center"
            >
              <BrandedQR value={url} size={180} />
              <p className="mt-2 text-lg font-bold text-gray-900">Mesa {n}</p>
              <button
                onClick={() => downloadOne(n)}
                data-testid={`garzon-download-${n}-btn`}
                className="mt-2 text-xs text-emerald-700 hover:underline"
              >
                Descargar PNG
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
